import React, { useCallback, useState } from 'react';

import { di } from 'react-magnetic-di';

import IconButton from '@atlaskit/button/icon/button';
import { cssMap } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import Radio from '@atlaskit/radio/radio';
import { token } from '@atlaskit/tokens';
import VisuallyHidden from '@atlaskit/visually-hidden/visually-hidden';

import { asNumberValue } from '../../../../../../../../common/types/form/form-fields/field-values/utils';
import { asFormRankingField } from '../../../../../../../../common/types/form/form-fields/utils';
import type { FormFieldComponentRendererProps } from '../../../../../../../../common/types/renderers';
import { ScrollContainer } from '../../../../../../../../common/ui/scroll-container';
import {
	getRatingIconComponent,
	getRatingIconFromOptions,
	getSelectedRatingIconComponent,
} from '../../../../../../../../common/utils/rating/utils';
import {
	useCurrentValue,
	useFieldInvalid,
	useFormField,
	useFormRendererActions,
} from '../../../../../../../../controllers/form-renderer-store';

const styles = cssMap({
	container: {
		paddingTop: token('space.100'),
		paddingBottom: token('space.100'),
	},
});

export const EditRatingInput = ({
	fieldId,
	fieldErrorId,
}: FormFieldComponentRendererProps): React.JSX.Element => {
	di(useCurrentValue, useFieldInvalid, useFormField, useFormRendererActions);

	const isInvalid = useFieldInvalid({ fieldId });
	const currentValue = asNumberValue(useCurrentValue({ fieldId }));
	const formField = useFormField({ fieldId });
	const formRankingField = asFormRankingField(formField);
	const { updateModifiedValue } = useFormRendererActions();
	const [hoveredOptionId, setHoveredOptionId] = useState<string | null>(null);

	const selectedOptionId = currentValue?.number?.toString() ?? null;
	const ratingIcon = getRatingIconFromOptions(formRankingField?.options);
	const UnselectedIcon = getRatingIconComponent(ratingIcon);
	const SelectedIcon = getSelectedRatingIconComponent(ratingIcon);

	const selectedNumericId = currentValue?.number ?? null;
	const hoveredNumericId = hoveredOptionId !== null ? Number(hoveredOptionId) : null;

	const onSelect = useCallback(
		(optionId: string): void => {
			if (!formRankingField) {
				return;
			}
			if (optionId === selectedOptionId) {
				updateModifiedValue(fieldId, null);
			} else {
				const selected = formRankingField.options.find((o) => o.id === optionId);
				if (selected) {
					updateModifiedValue(fieldId, { number: Number(optionId) });
				}
			}
		},
		[fieldId, formRankingField, selectedOptionId, updateModifiedValue],
	);

	if (formRankingField === undefined) {
		return <></>;
	}

	const isRatingScaleA11yFixesEnabled = fg('proforma_rating_scale_a11y_fixes');

	return (
		<ScrollContainer>
			<Box
				xcss={styles.container}
				testId="field-input-rating-edit"
				onMouseLeave={() => setHoveredOptionId(null)}
				aria-invalid={isInvalid}
				aria-describedby={isInvalid ? fieldErrorId : undefined}
			>
				<Inline
					space="space.400"
					role={isRatingScaleA11yFixesEnabled ? 'radiogroup' : undefined}
					aria-label={isRatingScaleA11yFixesEnabled ? formRankingField.label : undefined}
				>
					{formRankingField.options.map((option) => {
						const numericId = Number(option.id);
						const isFilledBySelection =
							selectedNumericId !== null &&
							!Number.isNaN(numericId) &&
							numericId <= selectedNumericId;
						const isFilledByHover =
							hoveredNumericId !== null &&
							!Number.isNaN(numericId) &&
							numericId <= hoveredNumericId;
						const isFilled = hoveredOptionId !== null ? isFilledByHover : isFilledBySelection;
						const IconComponent = isFilled ? SelectedIcon : UnselectedIcon;
						const inputId = `${fieldId}-${option.id}`;
						return (
							<Stack
								key={option.id}
								alignInline="center"
								space="space.100"
								testId={`field-input-rating-edit-option-${option.id}-${fieldId}`}
							>
								<VisuallyHidden>
									<Radio
										id={inputId}
										value={option.id}
										label={option.id}
										name={fieldId}
										isChecked={option.id === selectedOptionId}
										onChange={() => onSelect(option.id)}
									/>
								</VisuallyHidden>
								<IconButton
									appearance="subtle"
									icon={(iconProps) => (
										<IconComponent
											{...iconProps}
											color={isFilled ? token('color.icon.selected') : token('color.icon.subtle')}
										/>
									)}
									label={option.id}
									onClick={() => onSelect(option.id)}
									onMouseEnter={() => setHoveredOptionId(option.id)}
									testId={`field-input-rating-edit-option-button-${option.id}-${fieldId}`}
									aria-hidden
									tabIndex={-1}
								/>
								<Text size="small" align="center">
									{option.id}
								</Text>
							</Stack>
						);
					})}
				</Inline>
			</Box>
		</ScrollContainer>
	);
};
